import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';

/**
 * Smallest compatibility level at which `OPENJSON` / `FOR JSON` are available.
 * Below this, `Invalid object name 'OPENJSON'` is what users hit when applying
 * any wildcard / aggregate JSON filter — we use this constant to gate hints.
 */
export const MSSQL_OPENJSON_COMPAT_LEVEL = 130;

export const mssqlCompatLevelKey = (
  connectionId: string | null,
  database: string | null,
) => ['mssql-compat-level', connectionId, database] as const;

/**
 * Looks up the cached MSSQL `compatibility_level` for a (connection, database).
 * Returns `null` when the engine isn't MSSQL, when the connection isn't yet
 * established, or when the server-side lookup fails. The first call runs the
 * `sys.databases` query in main; subsequent calls are served from the main-
 * process cache so this is cheap to re-enable on every chip render.
 */
export function useMssqlCompatLevel(
  connectionId: string | null,
  database: string | null,
  engine: DatabaseEngineId | null | undefined,
) {
  return useQuery<number | null>({
    queryKey: mssqlCompatLevelKey(connectionId, database),
    queryFn: async () => {
      if (!connectionId || !database) return null;
      const api = window.electronAPI?.mssql;
      if (!api) return null;
      try {
        return await api.getDatabaseCompatLevel(connectionId, database);
      } catch {
        return null;
      }
    },
    enabled: Boolean(connectionId && database && engine === 'mssql'),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Returns a `recheck` callback that drops the main-process cache and
 * invalidates the TanStack Query entry for the given (connection, database),
 * plus an `isFetching` flag so callers can show a "Checking…" state. Used by
 * the OPENJSON warning surfaces (dropdown banner + path hint) to let users
 * pick up an externally-bumped `COMPATIBILITY_LEVEL` without waiting for the
 * 1-hour stale time.
 */
export function useMssqlCompatRecheck(
  connectionId: string | null,
  database: string | null,
) {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const key = mssqlCompatLevelKey(connectionId, database);
  const isFetching = queryClient.isFetching({ queryKey: key }) > 0;
  const recheck = useCallback(async () => {
    if (!connectionId || !database) return;
    track('feature:json_filter_recheck_compat');
    try {
      await window.electronAPI?.mssql?.forgetDatabaseCompatLevel(connectionId, database);
    } catch {
      // best-effort cache drop — invalidate refetches regardless
    }
    void queryClient.invalidateQueries({ queryKey: key });
  }, [connectionId, database, queryClient, track, key]);
  return { recheck, isFetching };
}

/**
 * Pure predicate: returns true when the engine is MSSQL and the cached compat
 * level is known to be below the OPENJSON threshold. False on non-MSSQL or
 * when the level isn't known yet (we don't want to block on null).
 */
export function isMssqlOpenJsonBlocked(
  engine: DatabaseEngineId | null | undefined,
  compatLevel: number | null | undefined,
): boolean {
  if (engine !== 'mssql') return false;
  if (compatLevel == null) return false;
  return compatLevel < MSSQL_OPENJSON_COMPAT_LEVEL;
}
